import React, { useEffect, useMemo, useState } from "react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";

export const PAGE_SIZE_OPTIONS = [50, 100, 250, 500, 1000, 2500, "all"];
const DEFAULT_PAGE_SIZE = 50;

/**
 * Reusable pagination state hook for any list-style page.
 *
 *   pageSize defaults to 50.
 *   "all" returns the full list (no slicing).
 *   Page resets to 1 whenever the underlying list length or page size changes.
 *
 * Returns {
 *   page, setPage,
 *   pageSize, setPageSize,
 *   visible — sliced array
 *   total   — items.length
 *   pageCount — total / pageSize (1 when 'all')
 * }
 */
export function usePagination(items = [], initialSize = DEFAULT_PAGE_SIZE) {
  const [pageSize, setPageSize] = useState(initialSize);
  const [page, setPage] = useState(1);

  // Reset to page 1 whenever the underlying dataset or page size changes
  useEffect(() => { setPage(1); }, [items.length, pageSize]);

  const total = items.length;
  const pageCount = pageSize === "all" ? 1 : Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(Math.max(1, page), pageCount);

  const visible = useMemo(() => {
    if (pageSize === "all") return items;
    const start = (safePage - 1) * pageSize;
    return items.slice(start, start + pageSize);
  }, [items, safePage, pageSize]);

  return {
    page: safePage, setPage,
    pageSize, setPageSize,
    visible, total, pageCount,
  };
}

/**
 * <PaginationControl> — page-size dropdown + page navigator.
 */
export default function PaginationControl({
  page, setPage, pageSize, setPageSize, total, pageCount,
  testIdPrefix = "pagination",
}) {
  if (total === 0) return null;

  const sizeLabel = (s) => (s === "all" ? "All" : String(s));
  const start = pageSize === "all" ? 1 : (page - 1) * pageSize + 1;
  const end = pageSize === "all" ? total : Math.min(page * pageSize, total);

  return (
    <div
      className="flex items-center gap-3 flex-wrap text-xs text-zinc-600"
      data-testid={`${testIdPrefix}-wrap`}
    >
      <span className="font-mono">
        <span data-testid={`${testIdPrefix}-range`}>{start}–{end}</span>{" "}
        <span className="text-zinc-400">of</span>{" "}
        <span data-testid={`${testIdPrefix}-total`}>{total}</span>
      </span>

      <Select value={String(pageSize)} onValueChange={(v) => setPageSize(v === "all" ? "all" : Number(v))}>
        <SelectTrigger
          className="rounded-sm h-8 w-[100px] text-xs"
          data-testid={`${testIdPrefix}-size`}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {PAGE_SIZE_OPTIONS.map((s) => (
            <SelectItem key={String(s)} value={String(s)}
              data-testid={`${testIdPrefix}-size-option-${s}`}>
              {sizeLabel(s)} / page
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {pageCount > 1 && (
        <div className="inline-flex items-center gap-1">
          <Button
            size="sm" variant="ghost" className="rounded-sm h-8 w-8 p-0"
            disabled={page === 1}
            onClick={() => setPage(1)}
            data-testid={`${testIdPrefix}-first`}
          >
            <ChevronsLeft className="h-3.5 w-3.5" />
          </Button>
          <Button
            size="sm" variant="ghost" className="rounded-sm h-8 w-8 p-0"
            disabled={page === 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            data-testid={`${testIdPrefix}-prev`}
          >
            <ChevronLeft className="h-3.5 w-3.5" />
          </Button>
          <span className="font-mono text-xs px-1"
            data-testid={`${testIdPrefix}-page-display`}>
            {page} / {pageCount}
          </span>
          <Button
            size="sm" variant="ghost" className="rounded-sm h-8 w-8 p-0"
            disabled={page === pageCount}
            onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
            data-testid={`${testIdPrefix}-next`}
          >
            <ChevronRight className="h-3.5 w-3.5" />
          </Button>
          <Button
            size="sm" variant="ghost" className="rounded-sm h-8 w-8 p-0"
            disabled={page === pageCount}
            onClick={() => setPage(pageCount)}
            data-testid={`${testIdPrefix}-last`}
          >
            <ChevronsRight className="h-3.5 w-3.5" />
          </Button>
        </div>
      )}
    </div>
  );
}
